import { useState, useCallback, useRef } from 'react'
import { useAutoRefresh } from './useAutoRefresh'
import { getSessionActivity } from '../lib/api'

export type ActivityState = 'working' | 'waiting' | 'idle'

/**
 * Polls terminal session activity state for activity badge display.
 * Returns a map of session name -> activity state (working/waiting/idle).
 *
 * Uses useAutoRefresh for 5-second polling with exponential backoff on errors.
 */
export function useTerminalActivity(
  appName: string,
  sessionNames: string[]
): Record<string, ActivityState> {
  const [activities, setActivities] = useState<Record<string, ActivityState>>({})

  // Use ref to avoid recreating fetchFn on sessionNames change
  const sessionNamesRef = useRef(sessionNames)
  sessionNamesRef.current = sessionNames

  const fetchActivity = useCallback(async () => {
    const sessions = sessionNamesRef.current
    if (!sessions.length || !appName) return

    const result = await getSessionActivity(appName, sessions)
    const map: Record<string, ActivityState> = {}
    for (const item of result) {
      map[item.session] = item.state
    }
    setActivities(map)
    // getSessionActivity throws on !response.ok, which enables useAutoRefresh backoff
  }, [appName])

  useAutoRefresh(fetchActivity, {
    baseInterval: 5000,
    maxInterval: 30000,
    enabled: sessionNames.length > 0 && !!appName,
  })

  return activities
}
